/**
 * Mirror of the orchestrator's i18n primitives, kept in sync at the
 * source path `services/orchestrator/src/i18n/localized-string.ts`.
 * The platform UI and APIs themselves stay English — these types
 * cover authoring + reading translations of configurable content
 * (currently question sets).
 */

export const SUPPORTED_LOCALES = ['en', 'fr', 'de', 'es', 'pt-BR', 'it', 'nl', 'et'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// English is implicit in `default` — these are the locales the
// editor surfaces as discrete variant inputs.
export const NON_DEFAULT_LOCALES = SUPPORTED_LOCALES.filter((l) => l !== 'en') as readonly Exclude<
  SupportedLocale,
  'en'
>[];

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  'pt-BR': 'Portuguese (Brazil)',
  it: 'Italian',
  nl: 'Dutch',
  et: 'Estonian',
};

export interface LocalizedString {
  default: string;
  translations?: Partial<Record<SupportedLocale, string>>;
}

export function isLocalizedStringShape(value: unknown): value is LocalizedString {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as LocalizedString).default === 'string'
  );
}

/**
 * Coerce a plain string OR a LocalizedString into the canonical
 * `{ default, translations }` shape used by the editor while the
 * operator is authoring. Saves at the end re-normalise via
 * `denormalizeForWire` to keep the wire payload small.
 */
export function toLocalized(value: string | LocalizedString | null | undefined): LocalizedString {
  if (value === null || value === undefined) return { default: '' };
  if (typeof value === 'string') return { default: value };
  return { default: value.default, translations: value.translations };
}

/**
 * Read the variant for a locale (English when `locale === 'en'`),
 * falling back to the default. Powers the "all variants" display
 * mode in the editor.
 */
export function getVariant(value: LocalizedString, locale: SupportedLocale): string {
  if (locale === 'en') return value.default;
  return value.translations?.[locale] ?? '';
}

export function setDefault(value: LocalizedString, text: string): LocalizedString {
  return { ...value, default: text };
}

/**
 * Set a translation for a non-default locale. An empty `text`
 * removes the entry — operators clearing a field shouldn't ship a
 * blank translation that overrides the fallback to "".
 */
export function setTranslation(
  value: LocalizedString,
  locale: SupportedLocale,
  text: string,
): LocalizedString {
  if (locale === 'en') return setDefault(value, text);
  const next: Partial<Record<SupportedLocale, string>> = { ...(value.translations ?? {}) };
  if (text === '') {
    delete next[locale];
  } else {
    next[locale] = text;
  }
  const empty = Object.keys(next).length === 0;
  return { default: value.default, ...(empty ? {} : { translations: next }) };
}

export function hasAnyTranslation(value: LocalizedString | string | null | undefined): boolean {
  if (!value || typeof value === 'string') return false;
  if (!value.translations) return false;
  return Object.values(value.translations).some((v) => typeof v === 'string' && v.length > 0);
}

/**
 * Collapse a LocalizedString to a plain string when no translations
 * are present (saves a few bytes on the wire and matches the
 * server's back-compat shape). Otherwise emits the full object.
 *
 * Used by callers right before they pass a value into a mutation
 * payload — internal editor state always stays in canonical form.
 */
export function denormalizeForWire(value: LocalizedString): string | LocalizedString {
  if (!value.translations || Object.keys(value.translations).length === 0) {
    return value.default;
  }
  return value;
}

/**
 * Split a LocalizedString into a `{ default, translations }` pair
 * suitable for the top-level QuestionSet columns
 * (`name` / `description` + `nameTranslations` / `descriptionTranslations`).
 * Returns `translations: undefined` when no translations exist so
 * the API client knows to omit the field.
 */
export function splitForColumns(value: LocalizedString): {
  default: string;
  translations: Partial<Record<SupportedLocale, string>> | undefined;
} {
  const t = value.translations;
  if (!t || Object.keys(t).length === 0) {
    return { default: value.default, translations: undefined };
  }
  return { default: value.default, translations: t };
}

/**
 * Build a LocalizedString from a (default, translations) pair as
 * returned by the API. Used for the top-level QuestionSet columns.
 */
export function joinFromColumns(
  defaultText: string | null | undefined,
  translations: Partial<Record<SupportedLocale, string>> | null | undefined,
): LocalizedString {
  return {
    default: defaultText ?? '',
    ...(translations && Object.keys(translations).length > 0 ? { translations } : {}),
  };
}

/**
 * Fold a consent type's text versions into a LocalizedString for the
 * authoring editor. Considers only rows in the given scope
 * (`organisation_id === organisationId`; defaults to the product-default
 * `null` scope), takes the highest-version body per locale, and maps the
 * English row to `default`. Unsupported locales and empty bodies are
 * dropped.
 */
export function localizedFromTextVersions(
  versions:
    | { organisation_id: string | null; version: number; locale: string; body: string }[]
    | undefined,
  organisationId: string | null = null,
): LocalizedString {
  const scoped = (versions ?? []).filter((v) => v.organisation_id === organisationId);
  const latestByLocale = new Map<string, { version: number; body: string }>();
  for (const v of scoped) {
    const current = latestByLocale.get(v.locale);
    if (!current || v.version > current.version) {
      latestByLocale.set(v.locale, { version: v.version, body: v.body });
    }
  }
  const translations: Partial<Record<SupportedLocale, string>> = {};
  for (const [locale, { body }] of latestByLocale) {
    if (locale === 'en' || !body) continue;
    if ((SUPPORTED_LOCALES as readonly string[]).includes(locale)) {
      translations[locale as SupportedLocale] = body;
    }
  }
  return {
    default: latestByLocale.get('en')?.body ?? '',
    ...(Object.keys(translations).length > 0 ? { translations } : {}),
  };
}
